import React, { useState, useEffect, useMemo } from 'react';
import {
  Card, Typography, Table, Button, Space, Modal, Form,
  Input, Upload, Image, Popconfirm, message, Row, Col, Tabs,
  Alert, Collapse, Tag, Checkbox,
} from 'antd';
import {
  UsergroupAddOutlined, PlusOutlined, EditOutlined,
  DeleteOutlined, UploadOutlined, SearchOutlined,
  TableOutlined, PictureOutlined, InboxOutlined,
  CheckCircleFilled, WarningFilled, CloseCircleFilled,
} from '@ant-design/icons';
import { familiarService, API_HOST } from '../../services/api';
import PlanillaCarga from '../../components/PlanillaCarga';

const { Title } = Typography;
const { TextArea } = Input;

// Charset permitido en rp_familiar (espejo del backend en familiar_routes.py).
// La carga masiva de fotos matchea por nombre de archivo, asi que el RP
// tiene que ser filename-safe.
const RP_REGEX = /^[A-Za-z0-9._-]+$/;

// Sanitiza un RP invalido para sugerir uno valido (quita acentos, reemplaza
// espacios por _, descarta cualquier otro caracter). Espejo de _sanitizar_rp
// del backend.
const sugerirRp = (rp) => {
  if (!rp) return '';
  // ̀-ͯ = combining diacritical marks (acentos NFD-decompuestos)
  const sinAcentos = rp.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const sinEspacios = sinAcentos.replace(/\s+/g, '_');
  return sinEspacios.replace(/[^A-Za-z0-9._-]/g, '');
};

const Familiares = () => {
  const [activeTab, setActiveTab]           = useState('catalogo');
  const [loading, setLoading]               = useState(false);
  const [familiares, setFamiliares]         = useState([]);
  const [pagination, setPagination]         = useState({ current: 1, pageSize: 10, total: 0 });
  const [search, setSearch]                 = useState('');
  const [searchInput, setSearchInput]       = useState('');
  const [modalVisible, setModalVisible]     = useState(false);
  const [editing, setEditing]               = useState(null);
  const [fileList, setFileList]             = useState([]);
  const [submitting, setSubmitting]         = useState(false);
  const [rpValue, setRpValue]               = useState('');
  const [serverErrorsMasivo, setServerErrorsMasivo] = useState([]);
  const [fotosFileList, setFotosFileList]   = useState([]);
  const [fotosUploading, setFotosUploading] = useState(false);
  const [fotosReporte, setFotosReporte]     = useState(null);  // null | resp.data
  const [form] = Form.useForm();

  useEffect(() => {
    fetchFamiliares();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, search]);

  const fetchFamiliares = async () => {
    setLoading(true);
    try {
      const params = { page: pagination.current, per_page: pagination.pageSize };
      if (search) params.search = search;
      const res = await familiarService.getAll(params);
      setFamiliares(res.data.familiares || []);
      setPagination(prev => ({ ...prev, total: res.data.total || 0 }));
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al cargar familiares');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    setSearch(searchInput.trim());
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const openModal = (familiar = null) => {
    setEditing(familiar);
    if (familiar) {
      form.setFieldsValue({
        rp_familiar: familiar.rp_familiar,
        nom_familiar: familiar.nom_familiar,
        premio_familiar: familiar.premio_familiar,
        mostrar_copa: familiar.mostrar_copa !== 0,
      });
      setRpValue(familiar.rp_familiar || '');
      setFileList(familiar.url_foto_familiar ? [{
        uid: '-1',
        name: 'foto_actual.jpg',
        status: 'done',
        url: `${API_HOST}${familiar.url_foto_familiar}`,
      }] : []);
    } else {
      form.resetFields();
      form.setFieldsValue({ mostrar_copa: true });
      setRpValue('');
      setFileList([]);
    }
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditing(null);
    form.resetFields();
    setRpValue('');
    setFileList([]);
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('rp_familiar', values.rp_familiar.trim());
      fd.append('nom_familiar', values.nom_familiar.trim());
      // Siempre enviar la key (aunque vaya vacia) para que al borrar el
      // premio se persista el vaciado. El backend hace (valor or None).
      fd.append('premio_familiar', (values.premio_familiar || '').trim());
      fd.append('mostrar_copa', values.mostrar_copa === false ? '0' : '1');
      if (fileList.length > 0 && fileList[0].originFileObj) {
        fd.append('foto', fileList[0].originFileObj);
      }
      if (editing) {
        await familiarService.update(editing.id_familiar, fd);
        message.success('Familiar actualizado');
      } else {
        await familiarService.create(fd);
        message.success('Familiar creado');
      }
      closeModal();
      fetchFamiliares();
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al guardar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await familiarService.delete(id);
      message.success('Familiar eliminado');
      // Limpiar busqueda + volver a pagina 1. Evita que la pantalla
      // quede vacia si lo borrado era el unico match del search.
      // Si search/pagination ya estaban en su valor base, el useEffect
      // no se redispara — en ese caso forzamos el refetch manual.
      const huboFiltro = search !== '' || pagination.current !== 1;
      setSearchInput('');
      setSearch('');
      setPagination(prev => ({ ...prev, current: 1 }));
      if (!huboFiltro) fetchFamiliares();
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al eliminar');
    }
  };

  // Columnas para la planilla de carga masiva. Validacion espejada del
  // backend: charset RP + duplicados intra-planilla + requireds.
  const planillaColumns = useMemo(() => [
    {
      key: 'rp_familiar',
      label: 'RP',
      required: true,
      width: 180,
      placeholder: 'M-001',
      validate: (value, allRows, rowId) => {
        if (!RP_REGEX.test(value)) {
          const s = sugerirRp(value);
          return s ? `Caracteres invalidos. ¿Quisiste decir ${s}?` : 'Caracteres invalidos en el RP';
        }
        const dup = allRows.find(r => r.id !== rowId && (r.cells.rp_familiar || '').trim() === value);
        if (dup) return 'RP duplicado en la planilla';
        return null;
      },
    },
    { key: 'nom_familiar',    label: 'Nombre',  required: true, width: 280, placeholder: 'Nombre del familiar' },
    { key: 'premio_familiar', label: 'Premio',  width: 320, placeholder: 'Premios y distinciones (opcional)' },
  ], []);

  const handleCargarMasivo = async (filas) => {
    setServerErrorsMasivo([]);
    try {
      const res = await familiarService.cargarCatalogoMasivo(filas);
      message.success(`${res.data.creados} familiares creados`);
      setActiveTab('catalogo');
      fetchFamiliares();
    } catch (error) {
      const data = error.response?.data;
      if (data?.errores?.length) {
        setServerErrorsMasivo(data.errores);
        message.error(`${data.filas_con_error} filas con errores. No se inserto nada.`);
      } else {
        message.error(data?.error || 'Error al cargar');
      }
    }
  };

  const handleSubirFotos = async () => {
    if (fotosFileList.length === 0) {
      message.warning('Agrega al menos una foto');
      return;
    }
    setFotosUploading(true);
    setFotosReporte(null);
    try {
      const res = await familiarService.cargarFotosMasivo(fotosFileList);
      setFotosReporte(res.data);
      if (res.data.matcheados > 0) {
        message.success(`${res.data.matcheados} fotos asociadas`);
      }
      // Si todo matcheo, limpiar la cola
      if (res.data.sin_match === 0 && res.data.extension_invalida === 0) {
        setFotosFileList([]);
      }
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al subir las fotos');
    } finally {
      setFotosUploading(false);
    }
  };

  // Sugerencia en vivo cuando el RP tiene caracteres invalidos
  const rpHint = useMemo(() => {
    if (!rpValue) return null;
    if (RP_REGEX.test(rpValue)) return null;
    const sugerencia = sugerirRp(rpValue);
    return sugerencia
      ? <span>Solo letras, numeros, <code>.</code>, <code>_</code>, <code>-</code>. ¿Quisiste decir <strong>{sugerencia}</strong>?</span>
      : <span>Solo letras, numeros, <code>.</code>, <code>_</code>, <code>-</code>.</span>;
  }, [rpValue]);

  const uploadProps = {
    onChange: ({ fileList: newList }) => setFileList(newList.slice(-1)),
    onRemove: () => setFileList([]),
    beforeUpload: (file) => {
      if (!file.type.startsWith('image/')) {
        message.error('Solo se permiten imagenes');
        return Upload.LIST_IGNORE;
      }
      if (file.size / 1024 / 1024 > 5) {
        message.error('La imagen debe ser menor a 5MB');
        return Upload.LIST_IGNORE;
      }
      return false;
    },
    fileList,
    listType: 'picture-card',
    maxCount: 1,
  };

  const columns = [
    {
      title: 'Foto',
      dataIndex: 'url_foto_familiar',
      key: 'foto',
      width: 80,
      render: (url) => url ? (
        <Image
          src={`${API_HOST}${url}`}
          width={50}
          height={50}
          style={{ objectFit: 'cover', borderRadius: 4 }}
        />
      ) : (
        <div style={{
          width: 50, height: 50, background: '#f0f0f0', borderRadius: 4,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#999', fontSize: 11,
        }}>
          Sin foto
        </div>
      ),
    },
    {
      title: 'RP',
      dataIndex: 'rp_familiar',
      key: 'rp_familiar',
      width: 140,
      render: (rp) => <code>{rp}</code>,
    },
    {
      title: 'Nombre',
      dataIndex: 'nom_familiar',
      key: 'nom_familiar',
    },
    {
      title: 'Premio',
      dataIndex: 'premio_familiar',
      key: 'premio_familiar',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 100,
      align: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button type="text" icon={<EditOutlined />} onClick={() => openModal(record)} />
          <Popconfirm
            title="¿Eliminar este familiar?"
            description="Tambien se quitaran las vinculaciones con animales."
            onConfirm={() => handleDelete(record.id_familiar)}
            okText="Si"
            cancelText="No"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const catalogoTab = (
    <Card>
      <Row justify="space-between" style={{ marginBottom: 16 }} gutter={[16, 16]}>
        <Col xs={24} sm={16} md={12}>
          <Input.Search
            placeholder="Buscar por RP o nombre..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onSearch={handleSearch}
            onPressEnter={handleSearch}
            enterButton={<SearchOutlined />}
            allowClear
            onClear={handleClearSearch}
          />
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
            Nuevo familiar
          </Button>
        </Col>
      </Row>

      <Table
        scroll={{ x: 'max-content' }}
        columns={columns}
        dataSource={familiares}
        rowKey="id_familiar"
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `Total: ${total} familiares`,
        }}
        onChange={(pag) => setPagination(prev => ({
          ...prev,
          current: pag.current,
          pageSize: pag.pageSize,
        }))}
      />
    </Card>
  );

  const masivoTab = (
    <Card>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
        Carga muchos familiares de una sola vez. Copia desde Excel/Sheets y
        pega en cualquier celda; o tipea directo. Las fotos se cargan despues
        desde la pestaña <strong>Fotos masivas</strong>.
      </Typography.Paragraph>
      <PlanillaCarga
        columns={planillaColumns}
        onSubmit={handleCargarMasivo}
        submitLabel="Cargar familiares"
        initialRowCount={8}
        serverErrors={serverErrorsMasivo}
      />
    </Card>
  );

  const fotosUploadProps = {
    multiple: true,
    fileList: fotosFileList,
    beforeUpload: () => false,  // no subir automatico, lo hace handleSubirFotos
    onChange: ({ fileList }) => setFotosFileList(fileList),
    onRemove: (file) => {
      setFotosFileList(prev => prev.filter(f => f.uid !== file.uid));
    },
    accept: 'image/*',
  };

  const fotosTab = (
    <Card>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Como funciona el match"
        description={
          <span>
            El nombre de cada archivo (sin extension) tiene que coincidir
            <strong> exactamente</strong> con el <code>RP</code> de un familiar
            del catalogo. Ej: <code>M-001.jpg</code> se asocia al familiar
            con RP <code>M-001</code>. Extensiones permitidas: jpg, jpeg, png,
            webp, gif.
            <br />
            <strong>Ojo:</strong> si el familiar ya tenia foto, esta la <strong>reemplaza</strong> (la
            vieja se borra del servidor). El reporte distingue 'nuevas' de
            'reemplazadas'.
          </span>
        }
      />

      <Upload.Dragger {...fotosUploadProps} disabled={fotosUploading}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Arrastra fotos aca o hace click para seleccionar</p>
        <p className="ant-upload-hint">
          Puedes seleccionar varias a la vez. Las que no matcheen se reportan,
          no bloquean a las que si.
        </p>
      </Upload.Dragger>

      <div style={{ marginTop: 16, textAlign: 'right' }}>
        <Space>
          {fotosFileList.length > 0 && (
            <Button onClick={() => setFotosFileList([])} disabled={fotosUploading}>
              Limpiar cola
            </Button>
          )}
          <Button
            type="primary"
            size="large"
            icon={<PictureOutlined />}
            loading={fotosUploading}
            disabled={fotosFileList.length === 0}
            onClick={handleSubirFotos}
          >
            Subir y matchear ({fotosFileList.length})
          </Button>
        </Space>
      </div>

      {fotosReporte && (
        <div style={{ marginTop: 24 }}>
          <Typography.Title level={4}>Resultado</Typography.Title>
          <Space wrap style={{ marginBottom: 16 }}>
            <Tag icon={<CheckCircleFilled />} color="success">
              {fotosReporte.matcheados} matcheados
            </Tag>
            {fotosReporte.nuevas > 0 && (
              <Tag color="processing">{fotosReporte.nuevas} nuevas</Tag>
            )}
            {fotosReporte.reemplazadas > 0 && (
              <Tag color="purple">{fotosReporte.reemplazadas} reemplazadas</Tag>
            )}
            {fotosReporte.sin_match > 0 && (
              <Tag icon={<WarningFilled />} color="warning">
                {fotosReporte.sin_match} sin match
              </Tag>
            )}
            {fotosReporte.extension_invalida > 0 && (
              <Tag icon={<CloseCircleFilled />} color="error">
                {fotosReporte.extension_invalida} extension invalida
              </Tag>
            )}
          </Space>

          <Collapse
            defaultActiveKey={fotosReporte.sin_match > 0 || fotosReporte.extension_invalida > 0
              ? ['sin-match', 'invalidas']
              : ['matcheados']}
            items={[
              fotosReporte.detalle.matcheados.length > 0 && {
                key: 'matcheados',
                label: <span><CheckCircleFilled style={{ color: '#52c41a' }} /> Matcheados ({fotosReporte.detalle.matcheados.length})</span>,
                children: (
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {fotosReporte.detalle.matcheados.map((m, i) => (
                      <li key={i}>
                        <code>{m.archivo}</code> {'→'} familiar <strong>{m.rp_familiar}</strong>
                        {m.accion === 'reemplazo' && (
                          <Tag color="purple" style={{ marginLeft: 8 }}>reemplazo</Tag>
                        )}
                        {m.accion === 'nueva' && (
                          <Tag color="processing" style={{ marginLeft: 8 }}>nueva</Tag>
                        )}
                      </li>
                    ))}
                  </ul>
                ),
              },
              fotosReporte.detalle.sin_match.length > 0 && {
                key: 'sin-match',
                label: <span><WarningFilled style={{ color: '#faad14' }} /> Sin match ({fotosReporte.detalle.sin_match.length})</span>,
                children: (
                  <>
                    <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
                      Estos archivos no encontraron un familiar con ese RP. Revisa
                      el nombre del archivo o crea el familiar primero.
                    </Typography.Paragraph>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {fotosReporte.detalle.sin_match.map((m, i) => (
                        <li key={i}>
                          <code>{m.archivo}</code> — buscaba RP <code>{m.rp_buscado}</code>
                        </li>
                      ))}
                    </ul>
                  </>
                ),
              },
              fotosReporte.detalle.extension_invalida.length > 0 && {
                key: 'invalidas',
                label: <span><CloseCircleFilled style={{ color: '#ff4d4f' }} /> Extension invalida ({fotosReporte.detalle.extension_invalida.length})</span>,
                children: (
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {fotosReporte.detalle.extension_invalida.map((m, i) => (
                      <li key={i}>
                        <code>{m.archivo}</code> — {m.motivo}
                      </li>
                    ))}
                  </ul>
                ),
              },
            ].filter(Boolean)}
          />

          {fotosReporte.matcheados > 0 && (
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <Button onClick={() => { setActiveTab('catalogo'); fetchFamiliares(); }}>
                Ver catalogo actualizado
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );

  return (
    <div>
      <Title level={2} style={{ marginBottom: 16 }}>
        <UsergroupAddOutlined /> Familiares
      </Title>

      <Tabs
        activeKey={activeTab}
        onChange={(k) => {
          setActiveTab(k);
          setServerErrorsMasivo([]);
          if (k !== 'fotos') setFotosReporte(null);
        }}
        items={[
          { key: 'catalogo', label: <span><TableOutlined /> Catalogo</span>,        children: catalogoTab },
          { key: 'masivo',   label: <span><PlusOutlined /> Carga masiva</span>,     children: masivoTab },
          { key: 'fotos',    label: <span><PictureOutlined /> Fotos masivas</span>, children: fotosTab },
        ]}
      />

      <Modal
        title={editing ? 'Editar familiar' : 'Nuevo familiar'}
        open={modalVisible}
        onCancel={closeModal}
        footer={null}
        width={600}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="rp_familiar"
            label="RP del familiar"
            extra={rpHint}
            rules={[
              { required: true, message: 'El RP es requerido' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (!RP_REGEX.test(value)) {
                    return Promise.reject(new Error('Caracteres invalidos en el RP'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              placeholder="Ej: M-001 o PIL.2024.03"
              onChange={(e) => setRpValue(e.target.value)}
              autoComplete="off"
            />
          </Form.Item>

          <Form.Item
            name="nom_familiar"
            label="Nombre"
            rules={[{ required: true, message: 'El nombre es requerido' }]}
          >
            <Input placeholder="Nombre del familiar" />
          </Form.Item>

          <Form.Item name="premio_familiar" label="Premio" style={{ marginBottom: 4 }}>
            <TextArea rows={2} placeholder="Premios o distinciones (opcional)" />
          </Form.Item>
          <Form.Item name="mostrar_copa" valuePropName="checked" style={{ marginBottom: 16, textAlign: 'right' }}>
            <Checkbox>Muestra copa 🏆</Checkbox>
          </Form.Item>

          <Form.Item label="Foto">
            <Upload {...uploadProps}>
              {fileList.length < 1 && (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>Subir foto</div>
                </div>
              )}
            </Upload>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={closeModal}>Cancelar</Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                {editing ? 'Guardar cambios' : 'Crear familiar'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Familiares;
